import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { renderIcon } from '../utils/iconRenderer';
import { FiArrowLeft, FiUpload, FiX, FiPlus } from 'react-icons/fi';
import { Category } from '../types/models';

const AddCategory: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategory: '',
    isTopCollection: false,
    isTopServices: false,
    collections: [] as string[],
    tags: [] as string[],
    images: [] as string[],
    videos: [] as string[]
  });

  const [newTag, setNewTag] = useState('');
  const [newCollection, setNewCollection] = useState('');
  const [newImage, setNewImage] = useState('');
  const [newVideo, setNewVideo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleAddCollection = () => {
    if (newCollection.trim() && !formData.collections.includes(newCollection.trim())) {
      setFormData(prev => ({
        ...prev,
        collections: [...prev.collections, newCollection.trim()]
      }));
      setNewCollection('');
    }
  };

  const handleRemoveCollection = (collection: string) => {
    setFormData(prev => ({
      ...prev,
      collections: prev.collections.filter(c => c !== collection)
    }));
  };

  const handleAddImage = () => {
    if (newImage.trim() && !formData.images.includes(newImage.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage.trim()]
      }));
      setNewImage('');
    }
  };

  const handleRemoveImage = (image: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== image)
    }));
  };

  const handleAddVideo = () => {
    if (newVideo.trim() && !formData.videos.includes(newVideo.trim())) {
      setFormData(prev => ({
        ...prev,
        videos: [...prev.videos, newVideo.trim()]
      }));
      setNewVideo('');
    }
  };

  const handleRemoveVideo = (video: string) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter(v => v !== video)
    }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (type === 'image') {
          setNewImage(result);
        } else {
          setNewVideo(result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Category name is required!');
      return;
    }

    setLoading(true);

    setTimeout(() => {
      const newCategory: Category = {
        id: Date.now().toString(),
        name: formData.name,
        description: formData.description,
        parentCategory: formData.parentCategory || undefined,
        isTopCollection: formData.isTopCollection,
        isTopServices: formData.isTopServices,
        collections: formData.collections,
        tags: formData.tags,
        images: formData.images,
        videos: formData.videos,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('New category:', newCategory);
      setLoading(false);
      alert('Category created successfully!');
      navigate('/category');
    }, 500);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/category')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            {renderIcon(FiArrowLeft, { size: 24 })}
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Add Category</h2>
            <p className="text-gray-600 mt-1">Create a new product category</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter category name"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Parent Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Parent Category
                </label>
                <select
                  name="parentCategory"
                  value={formData.parentCategory}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select parent category</option>
                  <option value="clothing">Clothing</option>
                  <option value="accessories">Accessories</option>
                  <option value="footwear">Footwear</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter category description"
                rows={4}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  name="isTopCollection"
                  checked={formData.isTopCollection}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-900">
                  Is Top Collection
                </label>
              </div>

              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  name="isTopServices"
                  checked={formData.isTopServices}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-900">
                  Is Top Services
                </label>
              </div>
            </div>
          </div>

          {/* Collections */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Collections</h3>

            <div className="flex gap-2">
              <input
                type="text"
                value={newCollection}
                onChange={e => setNewCollection(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddCollection())}
                placeholder="Enter collection name"
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleAddCollection}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {renderIcon(FiPlus, { size: 18 })}
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.collections.map((collection, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  <span>{collection}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCollection(collection)}
                    className="hover:text-blue-900"
                  >
                    {renderIcon(FiX, { size: 14 })}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Tags</h3>

            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Enter tag"
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {renderIcon(FiPlus, { size: 18 })}
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-green-900"
                  >
                    {renderIcon(FiX, { size: 14 })}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Images</h3>

            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newImage}
                  onChange={e => setNewImage(e.target.value)}
                  placeholder="Enter image URL or upload"
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddImage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {renderIcon(FiPlus, { size: 18 })}
                </button>
              </div>

              <label className="flex items-center justify-center w-full px-6 py-8 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer">
                <div className="text-center">
                  {renderIcon(FiUpload, { size: 32, className: 'text-gray-400 mx-auto mb-2' })}
                  <p className="text-sm font-medium text-gray-900">Click to upload image</p>
                  <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => handleFileUpload(e, 'image')}
                  className="hidden"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {formData.images.map((image, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={image}
                    alt={`${idx + 1}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(image)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    {renderIcon(FiX, { size: 14 })}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Videos */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Videos</h3>

            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newVideo}
                  onChange={e => setNewVideo(e.target.value)}
                  placeholder="Enter video URL or upload"
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddVideo}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {renderIcon(FiPlus, { size: 18 })}
                </button>
              </div>

              <label className="flex items-center justify-center w-full px-6 py-8 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer">
                <div className="text-center">
                  {renderIcon(FiUpload, { size: 32, className: 'text-gray-400 mx-auto mb-2' })}
                  <p className="text-sm font-medium text-gray-900">Click to upload video</p>
                  <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
                </div>
                <input
                  type="file"
                  accept="video/*"
                  onChange={e => handleFileUpload(e, 'video')}
                  className="hidden"
                />
              </label>
            </div>

            <div className="space-y-2">
              {formData.videos.map((video, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-gray-600 truncate">{video.substring(0, 50)}...</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveVideo(video)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    {renderIcon(FiX, { size: 16 })}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Category'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/category')}
              className="flex-1 px-6 py-3 bg-slate-200 text-gray-900 rounded-lg hover:bg-slate-300 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AddCategory;
